import { FC } from 'react'
import { Option, Select, Spinner, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useUserLandingPages } from '../hooks/useUserLandingPages'

const DEFAULT_VALUE = '__default__'

interface LandingPageSelectorProps {
  value: string | null | undefined
  onChange: (id: string | null) => void
  disabled?: boolean
}

const LandingPageSelector: FC<LandingPageSelectorProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { landingPages, defaultLandingPage, isLoading } = useUserLandingPages()

  if (isLoading) {
    return <Spinner className="h-4 w-4" />
  }

  if (landingPages.length === 0) return null

  const nonDefault = landingPages.filter((lp) => !lp.is_default)
  const selectedValue = value == null ? DEFAULT_VALUE : value

  // Flat option list — MT's Select trigger-display indexes into `children`
  // directly (no flattening), so passing a single Option + a mapped array
  // produces nested children and the trigger renders blank for items in the
  // mapped portion. Keep everything in one map to dodge that.
  const defaultLabel = t('upload.landingPage.defaultOption', {
    code: defaultLandingPage?.code ?? t('upload.landingPage.none'),
  })
  const options = [
    { value: DEFAULT_VALUE, label: defaultLabel },
    ...nonDefault.map((lp) => ({ value: lp.id, label: lp.code })),
  ]

  return (
    <div className="flex flex-col gap-1">
      <Select
        label={t('upload.landingPage.label')}
        value={selectedValue}
        onChange={(val) => onChange(val === DEFAULT_VALUE || !val ? null : val)}
        disabled={disabled}
        menuProps={{ className: 'dark:bg-primary' }}
        {...(darkMode && { color: 'blue-gray' })}
        labelProps={{
          className:
            'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
        }}
        // eslint-disable-next-line react/no-unstable-nested-components
        selected={(element) => (
          <span className="text-primary dark:text-white">{element?.props?.children ?? defaultLabel}</span>
        )}
        data-testid="agami-landing-page-select"
      >
        {options.map((opt) => (
          <Option
            key={opt.value}
            value={opt.value}
            className="text-primary dark:text-white dark:hover:bg-primary-300"
          >
            {opt.label}
          </Option>
        ))}
      </Select>
      <Typography variant="small" className="opacity-70">
        {t('upload.landingPage.help')}
      </Typography>
    </div>
  )
}

export default LandingPageSelector
